import { useEffect, useRef, useCallback } from 'react';
import { useTutorial } from '../../contexts/TutorialContext';
import './tutorial.css';

/**
 * TutorialOverlay - визуальный компонент туториала
 * 
 * Создаёт:
 * - Оверлей с вырезом для целевого элемента
 * - Подсветку целевого элемента
 * - Всплывающие подсказки с инструкциями
 * - Навигационные кнопки
 */

export default function TutorialOverlay() {
    const {
        isActive,
        currentStep,
        currentStepIndex,
        totalSteps,
        targetRect,
        isTransitioning,
        isFirstStep,
        isLastStep,
        nextStep,
        prevStep,
        skipTutorial,
    } = useTutorial();

    const tooltipRef = useRef(null);

    // Обработка нажатия Escape
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape') {
            skipTutorial();
        } else if (e.key === 'ArrowRight') {
            nextStep();
        } else if (e.key === 'ArrowLeft' && !isFirstStep) {
            prevStep();
        }
    }, [skipTutorial, nextStep, prevStep, isFirstStep]);

    useEffect(() => {
        if (isActive) {
            document.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = '';
        };
    }, [isActive, handleKeyDown]);

    // Расчёт позиции подсказки
    const getTooltipPosition = () => {
        if (!currentStep) {
            return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
        }

        const position = currentStep.position || 'center';

        // Если нет целевого элемента или позиция center - центрируем
        if (!targetRect || position === 'center') {
            return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
        }

        const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
        const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 768;
        const tooltipWidth = 360; // Минимальная ширина
        const tooltipHeight = 200; // Приблизительная высота
        const spacing = 16;

        let top, left;

        switch (position) {
            case 'top':
                top = targetRect.top - tooltipHeight - spacing;
                left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
                break;
            case 'bottom':
                top = targetRect.top + targetRect.height + spacing;
                left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
                break;
            case 'left':
                top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
                left = targetRect.left - tooltipWidth - spacing;
                break;
            case 'right':
                top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
                left = targetRect.left + targetRect.width + spacing;
                break;
            default:
                top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
                left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
        }

        // Проверяем границы viewport
        const minSpacing = 16;
        if (left < minSpacing) left = minSpacing;
        if (left + tooltipWidth > viewportWidth - minSpacing) {
            left = viewportWidth - tooltipWidth - minSpacing;
        }
        if (top < minSpacing) top = minSpacing;
        if (top + tooltipHeight > viewportHeight - minSpacing) {
            top = viewportHeight - tooltipHeight - minSpacing;
        }

        return { top: `${top}px`, left: `${left}px`, transform: 'none' };
    };

    // Получаем стили для выреза в оверлее
    const getHighlightStyles = () => {
        if (!targetRect) return null;

        const padding = 4;
        return {
            top: `${targetRect.top - padding}px`,
            left: `${targetRect.left - padding}px`,
            width: `${targetRect.width + padding * 2}px`,
            height: `${targetRect.height + padding * 2}px`,
        };
    };

    // Получаем класс позиции стрелки
    const getArrowPositionClass = () => {
        if (!targetRect) return '';
        return `tutorial-arrow-${currentStep?.position || 'center'}`;
    };

    // Прогресс бар
    const progressPercentage = totalSteps > 0 ? ((currentStepIndex + 1) / totalSteps) * 100 : 0;

    if (!isActive || !currentStep) return null;

    const tooltipPosition = getTooltipPosition();
    const highlightStyles = getHighlightStyles();

    return (
        <div 
            className={`tutorial-overlay ${isTransitioning ? 'tutorial-transitioning' : ''}`}
        >
            {/* SVG маска для оверлея с вырезом */}
            <svg className="tutorial-svg-overlay" xmlns="http://www.w3.org/2000/svg">
                <defs>
                    <mask id="tutorial-mask">
                        <rect width="100%" height="100%" fill="white" />
                        {highlightStyles && (
                            <rect
                                className="tutorial-highlight-rect"
                                x={parseFloat(highlightStyles.left)}
                                y={parseFloat(highlightStyles.top)}
                                width={parseFloat(highlightStyles.width)}
                                height={parseFloat(highlightStyles.height)}
                                rx="8"
                                ry="8"
                                fill="black"
                            />
                        )}
                    </mask>
                </defs>
                <rect
                    width="100%"
                    height="100%"
                    fill="rgba(0, 0, 0, 0.75)"
                    mask="url(#tutorial-mask)"
                />
            </svg>

            {/* Подсветка целевого элемента */}
            {highlightStyles && (
                <div
                    className="tutorial-highlight-area"
                    style={highlightStyles}
                >
                    <div className="tutorial-highlight-pulse" />
                </div>
            )}

            {/* Подсказка */}
            <div
                ref={tooltipRef}
                className={`tutorial-tooltip ${getArrowPositionClass()}`}
                style={tooltipPosition}
            >
                {/* Стрелка */}
                {targetRect && currentStep.position !== 'center' && (
                    <div className={`tutorial-arrow ${getArrowPositionClass()}`} />
                )}

                {/* Прогресс бар */}
                <div className="tutorial-progress">
                    <div 
                        className="tutorial-progress-bar"
                        style={{ width: `${progressPercentage}%` }}
                    />
                </div>

                {/* Индикатор шагов */}
                <div className="tutorial-step-indicator">
                    Шаг {currentStepIndex + 1} из {totalSteps}
                </div>

                {/* Заголовок */}
                <h3 className="tutorial-title">{currentStep.title}</h3>

                {/* Контент */}
                <p className="tutorial-content">{currentStep.content}</p>

                {/* Навигация */}
                <div className="tutorial-navigation">
                    <button
                        className="tutorial-btn tutorial-btn-skip"
                        onClick={skipTutorial}
                        type="button"
                    >
                        Пропустить
                    </button>

                    <div className="tutorial-nav-buttons">
                        {!isFirstStep && (
                            <button
                                className="tutorial-btn tutorial-btn-prev"
                                onClick={prevStep}
                                type="button"
                                aria-label="Предыдущий шаг"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="15 18 9 12 15 6"></polyline>
                                </svg>
                                Назад
                            </button>
                        )}

                        <button
                            className={`tutorial-btn ${isLastStep ? 'tutorial-btn-finish' : 'tutorial-btn-next'}`}
                            onClick={nextStep}
                            type="button"
                        >
                            {isLastStep ? (
                                <>
                                    Завершить
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                </>
                            ) : (
                                <>
                                    Далее
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="9 18 15 12 9 6"></polyline>
                                    </svg>
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Подсказка клавиш */}
                <div className="tutorial-keyboard-hint">
                    <span>Esc — выход</span>
                    <span>← → — навигация</span>
                </div>
            </div>
        </div>
    );
}
